import {
  Type,
  Injectable,
  inject,
  Injector,
  InjectionToken,
  createComponent,
  ApplicationRef,
  ComponentRef,
  OnDestroy,
  NgZone
} from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { CIPXDefaultDrawerComponent } from "./default-drawer.component";
import { FuseDrawerMode, FuseDrawerPosition } from "@fuse/components/drawer/drawer.types";
import { CIPXDrawerContentInterface, ExtractDrawerData } from './drawer-content.interface';
import { fromEvent, Subject, takeUntil } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';

export const DRAWER_DATA = new InjectionToken<any>('DRAWER_DATA');
export const DRAWER_REF = new InjectionToken<DrawerRef<any>>('DRAWER_REF');

export interface DrawerConfig<T = any> {
  data?: T;
  position?: FuseDrawerPosition;
  mode?: FuseDrawerMode;
  sizeType?: 'small' | 'half' | 'large';
  disableOverlayClose?: boolean;
  transparentOverlay?: boolean;
  fixed?: boolean;
  appendToBody?: boolean;
  title?: string;
  showBackButton?: boolean;
}

/**
 * Reference to an opened drawer
 * @template C The content component type
 */
export class DrawerRef<C extends CIPXDrawerContentInterface<any> = CIPXDrawerContentInterface<any>> {
  constructor(
    public componentInstance: C,
    public data: ExtractDrawerData<C>,
    private _drawerRef: ComponentRef<CIPXDefaultDrawerComponent>,
    private _contentComponentRef: ComponentRef<C>
  ) {}

  close() {
    if (this._drawerRef.instance) {
      this._drawerRef.instance.close();
    }
  }

  setTitle(title: string) {
    if (this._drawerRef.instance) {
      this._drawerRef.instance.title = title;
      // Force change detection
      this._drawerRef.changeDetectorRef.detectChanges();
    }
  }

  setShowBackButton(show: boolean) {
    if (this._drawerRef.instance) {
      this._drawerRef.instance.showBackButton = show;
      // Force change detection
      this._drawerRef.changeDetectorRef.detectChanges();
    }
  }

  setShowHeader(show: boolean) {
    if (this._drawerRef.instance) {
      this._drawerRef.instance.showHeader = show;
      // Force change detection
      this._drawerRef.changeDetectorRef.detectChanges();
    }
  }

  setTransparentOverlay(transparent: boolean) {
    if (this._drawerRef.instance) {
      // console.log(`Setting transparentOverlay to: ${transparent}`);
      this._drawerRef.instance.transparentOverlay = transparent;

      // Also set on the drawer template if available
      if (this._drawerRef.instance.drawerTemplate) {
        this._drawerRef.instance.drawerTemplate.transparentOverlay = transparent;
        // console.log(`Also set on drawerTemplate: ${this._drawerRef.instance.drawerTemplate.transparentOverlay}`);

        // Also try to set on the underlying Fuse drawer
        if (this._drawerRef.instance.drawerTemplate.fuseDrawer) {
          this._drawerRef.instance.drawerTemplate.fuseDrawer.transparentOverlay = transparent;
          // console.log(`Also set on fuseDrawer: ${this._drawerRef.instance.drawerTemplate.fuseDrawer.transparentOverlay}`);
          // Trigger change detection on the Fuse drawer as well
          (this._drawerRef.instance.drawerTemplate.fuseDrawer as any)._changeDetectorRef?.detectChanges();
        }
      }

      // Force change detection
      this._drawerRef.changeDetectorRef.detectChanges();
      // console.log(`transparentOverlay after change: ${this._drawerRef.instance.transparentOverlay}`);
    }
  }

  setDisableOverlay(disable: boolean) {
    if (this._drawerRef.instance) {
      // console.log(`Setting disableOverlay to: ${disable}`);
      this._drawerRef.instance.disableOverlay = disable;

      // Also set on the drawer template if available
      if (this._drawerRef.instance.drawerTemplate) {
        this._drawerRef.instance.drawerTemplate.disableOverlay = disable;
        // console.log(`Also set on drawerTemplate: ${this._drawerRef.instance.drawerTemplate.disableOverlay}`);

        // Also try to set on the underlying Fuse drawer
        if (this._drawerRef.instance.drawerTemplate.fuseDrawer) {
          const fuseDrawer = this._drawerRef.instance.drawerTemplate.fuseDrawer;
          fuseDrawer.disableOverlay = disable;
          // console.log(`Also set on fuseDrawer: ${fuseDrawer.disableOverlay}`);
          // console.log(`FuseDrawer mode: ${fuseDrawer.mode}, opened: ${fuseDrawer.opened}`);

          // Trigger change detection on the Fuse drawer as well
          (fuseDrawer as any)._changeDetectorRef?.detectChanges();

          // If we're enabling overlay and drawer is open, manually trigger overlay show
          if (!disable && fuseDrawer.opened && fuseDrawer.mode === 'over') {
            // console.log('Manually triggering overlay show');
            (fuseDrawer as any)._showOverlay?.();
          }
          // If we're disabling overlay, manually trigger overlay hide
          else if (disable && fuseDrawer.opened && fuseDrawer.mode === 'over') {
            // console.log('Manually triggering overlay hide');
            (fuseDrawer as any)._hideOverlay?.();
          }
        }
      }

      // Force change detection
      this._drawerRef.changeDetectorRef.detectChanges();
      // console.log(`disableOverlay after change: ${this._drawerRef.instance.disableOverlay}`);
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class CIPXDrawerManagerService implements OnDestroy {
  private _injector = inject(Injector);
  private _appRef = inject(ApplicationRef);
  private _document = inject(DOCUMENT);
  private _activeDrawers: ComponentRef<CIPXDefaultDrawerComponent>[] = [];
  private _baseZIndex = 300;
  private _appendToBody = true;
  private _destroy$ = new Subject<void>();
  private _escapeKeySubscription: any;
  private _ngZone = inject(NgZone);
  private _router = inject(Router);

  constructor() {
    this._setupEscapeKeyListener();
    this._setupScrollPrevention();

    this._router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.closeAllDrawers();
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Setup global escape key listener
   */
  private _setupEscapeKeyListener(): void {
    // Run outside Angular zone to prevent change detection on every keydown event
    this._ngZone.runOutsideAngular(() => {
      fromEvent<KeyboardEvent>(this._document, 'keydown')
        .pipe(takeUntil(this._destroy$))
        .subscribe(async (event) => {
          if (event.key === 'Escape' && this._activeDrawers.length > 0) {
            // Get the top-level drawer
            const topDrawer = this._activeDrawers[this._activeDrawers.length - 1];

            // Check if there's an active element that might want to handle escape
            const activeElement = this._document.activeElement as HTMLElement;

            // If there's an input/textarea/select with a value, don't close the drawer
            if (activeElement &&
                (activeElement.tagName === 'INPUT' ||
                 activeElement.tagName === 'TEXTAREA' ||
                 activeElement.tagName === 'SELECT')) {
              // Check if the input has a value
              if ((activeElement as HTMLInputElement).value) {
                return;
              }
            }

            if (topDrawer?.instance) {
              event.preventDefault();
              event.stopPropagation();

              // Run the close operation back in Angular zone
              this._ngZone.run(async () => {
                await topDrawer.instance.close();
              });
            }
          }
        });
    });
  }

  /**
   * Setup scroll prevention for drawer content
   */
  private _setupScrollPrevention(): void {
    // Run outside Angular zone to prevent change detection on every wheel event
    this._ngZone.runOutsideAngular(() => {
      fromEvent<WheelEvent>(this._document, 'wheel', { capture: true, passive: false })
        .pipe(takeUntil(this._destroy$))
        .subscribe((event) => {
          // Check if we have any active drawers
          if (this._activeDrawers.length === 0) return;

          // Check if the event target is inside a drawer scrollable content
          const target = event.target as HTMLElement;
          const scrollableContent = target.closest('.cipx-scrollable-content');

          if (scrollableContent) {
            // Check if we're inside an active drawer
            const isInDrawer = this._activeDrawers.some(drawer =>
              drawer.location.nativeElement.contains(scrollableContent)
            );

            if (isInDrawer) {
                                          // If scrollable content has no-overflow class (mobile mode), don't interfere with scroll
              const hasNoOverflow = scrollableContent.classList.contains('no-overflow');

              if (hasNoOverflow) {
                // On mobile with no-overflow, don't prevent scroll - let the CSS handle it
                return;
              }

              const { scrollTop, scrollHeight, clientHeight } = scrollableContent;
              const delta = event.deltaY;

              // Only prevent scroll if we're at the absolute boundaries AND trying to scroll beyond
              const isAtTop = scrollTop <= 1; // Small tolerance for rounding
              const isAtBottom = scrollTop >= scrollHeight - clientHeight - 1;
              const isScrollingUp = delta < 0;
              const isScrollingDown = delta > 0;

              // Only prevent when at boundary AND trying to scroll beyond it
              if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
                event.preventDefault();
                event.stopPropagation();
              }
              // Always stop propagation to prevent body scroll, but allow internal scrolling
              else {
                event.stopPropagation();
              }
            }
          }
        });
    });
  }

  /**
   * Configure where drawers should be appended in the DOM
   */
  setAppendToBody(appendToBody: boolean): void {
    this._appendToBody = appendToBody;
  }

  /**
   * Open a drawer with a content component that implements CIPXDrawerContentInterface
   * The data type is automatically inferred from the component's interface
   *
   * @param contentComponent Component class that implements CIPXDrawerContentInterface<TData>
   * @param config Drawer configuration options with properly typed data
   * @returns DrawerRef for controlling the drawer
   */
  openDrawer<C extends CIPXDrawerContentInterface<any>>(
    contentComponent: Type<C>,
    config?: DrawerConfig<ExtractDrawerData<C>>
  ): DrawerRef<C> {
    const finalConfig = config || {};

    // Create the drawer component
    const drawerRef = createComponent(CIPXDefaultDrawerComponent, {
      environmentInjector: this._appRef.injector
    });

    // Configure the drawer BEFORE attaching view
    const drawer = drawerRef.instance;
    drawer.position = finalConfig.position ?? 'right';
    drawer.mode = finalConfig.mode ?? 'over';
    drawer.sizeType = finalConfig.sizeType ?? 'half';
    drawer.showUpBand = true;
    drawer.disableOverlay = true;
    drawer.disableOverlayClose = finalConfig.disableOverlayClose ?? true;
    drawer.transparentOverlay = finalConfig.transparentOverlay ?? true;
    drawer.fixed = finalConfig.fixed ?? true;
    drawer.title = finalConfig.title ?? '';
    drawer.showBackButton = finalConfig.showBackButton ?? false;
    drawer.opened = false;

    // Attach drawer view AFTER configuration
    this._appRef.attachView(drawerRef.hostView);

    // Get drawer DOM element and container
    const drawerElement = drawerRef.location.nativeElement;
    const containerElement = this._getContainerElement(finalConfig.appendToBody);
    containerElement.appendChild(drawerElement);

    // Update z-index
    const zIndex = this._baseZIndex + (this._activeDrawers.length * 10);
    drawerElement.style.zIndex = zIndex.toString();

    // Create drawer ref early so we can inject it
    const drawerRefObj = new DrawerRef<C>(
      null!, // Will be set later
      finalConfig.data as ExtractDrawerData<C>,
      drawerRef,
      null! // Will be set later
    );

    // Create injector with drawer data and drawer ref
    const contentInjector = Injector.create({
      parent: this._injector,
      providers: [
        { provide: DRAWER_DATA, useValue: finalConfig.data },
        { provide: DRAWER_REF, useValue: drawerRefObj }
      ]
    });

    // Create the content component
    const contentComponentRef = createComponent(contentComponent, {
      environmentInjector: this._appRef.injector,
      elementInjector: contentInjector
    });

    // Update drawer ref with actual component instance
    drawerRefObj.componentInstance = contentComponentRef.instance;
    (drawerRefObj as any)._contentComponentRef = contentComponentRef;

    // Don't attach content component view to ApplicationRef
    // The ViewContainerRef will handle attaching it

    // Track active drawer
    this._activeDrawers.push(drawerRef);

    // Wait for the next animation frame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Force a tick to ensure view is initialized
      this._appRef.tick();

      // Set the content in the drawer
      drawer.setContent(contentComponentRef);

      // Open the drawer after content is set
      setTimeout(() => {
        drawer.open();
      }, 50);

      // Listen for drawer close event
      const subscription = drawer.closeRequested.subscribe(() => {
        subscription.unsubscribe();
        this._removeDrawer(drawerRef, contentComponentRef);
      });
    });

    return drawerRefObj;
  }

  private _removeDrawer(drawerRef: ComponentRef<CIPXDefaultDrawerComponent>, contentComponentRef: ComponentRef<any>): void {
    const index = this._activeDrawers.indexOf(drawerRef);
    if (index > -1) {
      this._activeDrawers.splice(index, 1);
    }

    // Give time for close animation
    setTimeout(() => {
      // Destroy content component (this will also detach it)
      contentComponentRef.destroy();

      // Detach and destroy drawer component
      this._appRef.detachView(drawerRef.hostView);
      drawerRef.destroy();

      // Remove from DOM
      const drawerElement = drawerRef.location.nativeElement;
      if (drawerElement.parentNode) {
        drawerElement.parentNode.removeChild(drawerElement);
      }
    }, 400);
  }

  private _getContainerElement(overrideAppendToBody?: boolean): HTMLElement {
    const shouldAppendToBody = overrideAppendToBody ?? this._appendToBody;

    if (shouldAppendToBody) {
      return this._document.body;
    }

    // Get the Angular root element
    const rootComponent = this._appRef.components[0];
    if (rootComponent) {
      return rootComponent.location.nativeElement as HTMLElement;
    }

    // Fallback to body if no root component found
    return this._document.body;
  }

  closeAllDrawers(): void {
    const drawersToClose = [...this._activeDrawers];

    drawersToClose.forEach(async (drawerRef) => {
      if (drawerRef?.instance) {
        await drawerRef.instance.close();
      }
    });
  }

  /**
   * Replace the content component of an existing drawer
   * @param drawerRef The existing drawer reference
   * @param newContentComponent The new component class to replace with
   * @param newConfig Optional new configuration for the drawer
   * @returns Updated DrawerRef with the new component
   */
  replaceDrawerContent<C extends CIPXDrawerContentInterface<any>>(
    drawerRef: DrawerRef<any>,
    newContentComponent: Type<C>,
    newConfig?: Partial<DrawerConfig<ExtractDrawerData<C>>>
  ): DrawerRef<C> {
    if (!drawerRef || !drawerRef.componentInstance) {
      throw new Error('Invalid drawer reference provided');
    }

    const currentDrawerComponent = (drawerRef as any)._drawerRef;
    const currentContentComponent = (drawerRef as any)._contentComponentRef;

    if (!currentDrawerComponent || !currentContentComponent) {
      throw new Error('Drawer reference is missing required component references');
    }

    // Update drawer configuration if provided
    if (newConfig) {
      const drawer = currentDrawerComponent.instance;
      if (newConfig.title !== undefined) drawer.title = newConfig.title;
      if (newConfig.showBackButton !== undefined) drawer.showBackButton = newConfig.showBackButton;
      if (newConfig.position !== undefined) drawer.position = newConfig.position;
      if (newConfig.mode !== undefined) drawer.mode = newConfig.mode;
      if (newConfig.sizeType !== undefined) drawer.sizeType = newConfig.sizeType;
      if (newConfig.disableOverlayClose !== undefined) drawer.disableOverlayClose = newConfig.disableOverlayClose;
      if (newConfig.transparentOverlay !== undefined) drawer.transparentOverlay = newConfig.transparentOverlay;
      if (newConfig.fixed !== undefined) drawer.fixed = newConfig.fixed;
    }

    // Create new drawer ref object with updated data
    const newDrawerRef = new DrawerRef<C>(
      null!, // Will be set later
      newConfig?.data as ExtractDrawerData<C> || drawerRef.data,
      currentDrawerComponent,
      null! // Will be set later
    );

    // Create injector with new drawer data and drawer ref
    const contentInjector = Injector.create({
      parent: this._injector,
      providers: [
        { provide: DRAWER_DATA, useValue: newConfig?.data || drawerRef.data },
        { provide: DRAWER_REF, useValue: newDrawerRef }
      ]
    });

    // Create the new content component
    const newContentComponentRef = createComponent(newContentComponent, {
      environmentInjector: this._appRef.injector,
      elementInjector: contentInjector
    });

    // Update drawer ref with new component instance
    newDrawerRef.componentInstance = newContentComponentRef.instance;
    (newDrawerRef as any)._contentComponentRef = newContentComponentRef;

    // Wait for the next animation frame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Force a tick to ensure view is initialized
      this._appRef.tick();

      // Destroy the old content component
      currentContentComponent.destroy();

      // Set the new content in the drawer
      currentDrawerComponent.instance.setContent(newContentComponentRef);

      // Trigger change detection
      currentDrawerComponent.changeDetectorRef.detectChanges();

      // Call lifecycle hooks if they exist
      if (newContentComponentRef.instance.afterDrawerOpened) {
        setTimeout(() => {
          newContentComponentRef.instance.afterDrawerOpened!();
        }, 100);
      }
    });

    return newDrawerRef;
  }

}
